import type { AssistantPromptMode, AssistantPromptTurn, AssistantSuggestion, AssistantThreadAnalysis } from '~/types/models';

type CachedAnalysisEntry = {
    fingerprint: string;
    analysis: AssistantThreadAnalysis;
    savedAt: number;
};

type LegacyDismissedSuggestionEntry = {
    fingerprint: string;
    signatures: string[];
};

type ResolvedSuggestionEntry = {
    fingerprint?: string;
    signature: string;
    sourceMessageIds: string[];
    handledMessages?: boolean;
};

type PromptSessionState = {
    turns: AssistantPromptTurn[];
    clientId: string;
    mode: AssistantPromptMode;
    savedAt: number;
};

type AssistantMemoryState = {
    cache: Record<string, CachedAnalysisEntry>;
    dismissed: Record<string, LegacyDismissedSuggestionEntry>;
    resolved: Record<string, ResolvedSuggestionEntry[]>;
    promptSession: PromptSessionState;
};

const ASSISTANT_CLIENT_CACHE_VERSION = 'guarded-v1';
const ASSISTANT_CLIENT_CACHE_MAX_AGE_MS = 15 * 60 * 1000;
const ASSISTANT_PROMPT_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function createEmptyState(): AssistantMemoryState {
    return {
        cache: {},
        dismissed: {},
        resolved: {},
        promptSession: {
            turns: [],
            clientId: '',
            mode: 'suggestions',
            savedAt: 0,
        },
    };
}

function toMillis(value: unknown): number {
    if (!value) return 0;
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
        return (value as { toDate: () => Date }).toDate().getTime();
    }
    if (typeof value === 'object' && value !== null && 'seconds' in value) {
        return Number((value as { seconds: number }).seconds) * 1000;
    }
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    const parsed = new Date(String(value)).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
}

function suggestionSignature(suggestion: AssistantSuggestion): string {
    return [
        suggestion.kind,
        suggestion.title,
        suggestion.summary,
        suggestion.suggestedProjectName || '',
        suggestion.suggestedTaskTitle || '',
        suggestion.suggestedReply?.slice(0, 160) || '',
    ]
        .join('::')
        .trim()
        .toLowerCase();
}

export function assistantThreadFingerprint(lastAt: unknown, count = 0): string {
    return `${ASSISTANT_CLIENT_CACHE_VERSION}:${count}:${toMillis(lastAt)}`;
}

export function assistantResolvedSuggestionMatches(
    entry: Pick<ResolvedSuggestionEntry, 'fingerprint' | 'signature' | 'sourceMessageIds'>,
    fingerprint: string,
    signature: string,
    sourceMessageIds: string[],
): boolean {
    if (!entry.fingerprint || entry.fingerprint !== fingerprint || entry.signature !== signature) {
        return false;
    }

    if (entry.sourceMessageIds.length === 0 && sourceMessageIds.length === 0) {
        return true;
    }

    return sourceMessageIds.length > 0
        && sourceMessageIds.every(id => entry.sourceMessageIds.includes(id));
}

export function assistantHandledMessageIdsForFingerprint(
    entries: ResolvedSuggestionEntry[],
    fingerprint: string,
): string[] {
    const handled = entries
        .filter(entry => entry.fingerprint === fingerprint && entry.handledMessages === true)
        .flatMap(entry => entry.sourceMessageIds);

    return [...new Set(handled)];
}

export function useAssistantMemory() {
    const tenantStore = useTenantStore();
    const authStore = useAuthStore();
    const userStore = useUserStore();
    const { $auth } = useNuxtApp();

    const memory = ref<AssistantMemoryState>(createEmptyState());
    const storagePrefix = computed(() => {
        const tenantId = tenantStore.currentTenantId || '';
        const userId = authStore.user?.uid || userStore.user?.id || $auth.currentUser?.uid || '';
        if (!tenantId || !userId) return '';
        return `blossum:assistant:${tenantId}:${userId}:`;
    });
    const storageKey = computed(() => {
        if (!storagePrefix.value) return '';
        const dataVersion = Math.max(
            0,
            Number(tenantStore.currentTenant?.communicationSettings?.assistantMonitoring?.dataVersion || 0),
        );
        return `${storagePrefix.value}${dataVersion}`;
    });

    function loadMemory() {
        if (!import.meta.client || !storageKey.value) {
            memory.value = createEmptyState();
            return;
        }

        for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
            const key = window.localStorage.key(index);
            if (key?.startsWith(storagePrefix.value) && key !== storageKey.value) {
                window.localStorage.removeItem(key);
            }
        }

        const raw = window.localStorage.getItem(storageKey.value);
        if (!raw) {
            memory.value = createEmptyState();
            return;
        }

        try {
            const parsed = JSON.parse(raw) as Partial<AssistantMemoryState>;
            const now = Date.now();
            const promptSavedAt = Number(parsed.promptSession?.savedAt || 0);
            const promptSessionFresh = promptSavedAt > 0
                && (now - promptSavedAt) <= ASSISTANT_PROMPT_SESSION_MAX_AGE_MS;
            const freshCache = Object.fromEntries(
                Object.entries(parsed.cache || {}).filter(([, entry]) => (
                    entry
                    && (now - Number(entry.savedAt || 0)) <= ASSISTANT_CLIENT_CACHE_MAX_AGE_MS
                )),
            );
            memory.value = {
                cache: freshCache,
                dismissed: parsed.dismissed || {},
                resolved: parsed.resolved || {},
                promptSession: {
                    turns: promptSessionFresh && Array.isArray(parsed.promptSession?.turns) ? parsed.promptSession.turns : [],
                    clientId: promptSessionFresh ? String(parsed.promptSession?.clientId || '') : '',
                    mode: promptSessionFresh && parsed.promptSession?.mode === 'chat' ? 'chat' : 'suggestions',
                    savedAt: promptSessionFresh ? promptSavedAt : 0,
                },
            };
            persistMemory();
        } catch {
            memory.value = createEmptyState();
        }
    }

    function persistMemory() {
        if (!import.meta.client || !storageKey.value) return;
        window.localStorage.setItem(storageKey.value, JSON.stringify(memory.value));
    }

    function getCachedAnalysis(clientId: string, fingerprint: string): AssistantThreadAnalysis | null {
        const entry = memory.value.cache[clientId];
        if (
            !entry
            || entry.fingerprint !== fingerprint
            || (Date.now() - Number(entry.savedAt || 0)) > ASSISTANT_CLIENT_CACHE_MAX_AGE_MS
        ) {
            return null;
        }

        return entry.analysis;
    }

    function setCachedAnalysis(clientId: string, fingerprint: string, analysis: AssistantThreadAnalysis) {
        memory.value = {
            ...memory.value,
            cache: {
                ...memory.value.cache,
                [clientId]: {
                    fingerprint,
                    analysis,
                    savedAt: Date.now(),
                },
            },
        };
        persistMemory();
    }

    async function getAvailableCachedAnalysis(
        clientId: string,
        fingerprint: string,
    ): Promise<AssistantThreadAnalysis | null> {
        const local = getCachedAnalysis(clientId, fingerprint);
        if (local) return local;

        const tenantId = String(tenantStore.currentTenantId || '').trim();
        if (!tenantId || !clientId || !fingerprint) return null;

        try {
            const token = await $auth.currentUser?.getIdToken();
            if (!token) return null;

            const result = await $fetch<{ analysis?: AssistantThreadAnalysis | null }>('/api/ai/analyze-thread', {
                method: 'POST',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: {
                    tenantId,
                    clientId,
                    handledMessageIds: getHandledMessageIds(clientId, fingerprint),
                    threadFingerprint: fingerprint,
                    cacheOnly: true,
                },
            });
            const shared = result.analysis || null;
            if (shared) {
                setCachedAnalysis(clientId, fingerprint, shared);
            }
            return shared;
        } catch {
            return null;
        }
    }

    function resolveSuggestions(
        clientId: string,
        fingerprint: string,
        suggestions: AssistantSuggestion[],
        markSourceMessagesHandled = false,
    ) {
        if (!clientId || suggestions.length === 0) return;

        const currentResolved = memory.value.resolved[clientId] || [];
        const nextResolved = [...currentResolved];
        for (const suggestion of suggestions) {
            const signature = suggestionSignature(suggestion);
            const sourceMessageIds = (suggestion.sourceMessageIds || []).filter(Boolean);
            const alreadyExists = nextResolved.some((entry) => (
                assistantResolvedSuggestionMatches(entry, fingerprint, signature, sourceMessageIds)
                && entry.sourceMessageIds.length === sourceMessageIds.length
            ));

            if (!alreadyExists) {
                nextResolved.push({
                    fingerprint,
                    signature,
                    sourceMessageIds,
                    handledMessages: markSourceMessagesHandled,
                });
            }
        }

        memory.value = {
            ...memory.value,
            resolved: {
                ...memory.value.resolved,
                [clientId]: nextResolved,
            },
        };
        persistMemory();
    }

    function dismissSuggestion(clientId: string, fingerprint: string, suggestion: AssistantSuggestion) {
        resolveSuggestions(clientId, fingerprint, [suggestion], false);
    }

    function clearDismissedSuggestions(clientId: string) {
        const nextResolved = { ...memory.value.resolved };
        const nextDismissed = { ...memory.value.dismissed };

        delete nextResolved[clientId];
        delete nextDismissed[clientId];

        memory.value = {
            ...memory.value,
            resolved: nextResolved,
            dismissed: nextDismissed,
        };
        persistMemory();
    }

    function getHandledMessageIds(clientId: string, fingerprint: string): string[] {
        return assistantHandledMessageIdsForFingerprint(
            memory.value.resolved[clientId] || [],
            fingerprint,
        );
    }

    function getPromptSession(): PromptSessionState {
        return memory.value.promptSession || createEmptyState().promptSession;
    }

    function setPromptSession(session: Partial<PromptSessionState>) {
        const turns = Array.isArray(session.turns)
            ? session.turns.slice(-40)
            : memory.value.promptSession.turns;

        memory.value = {
            ...memory.value,
            promptSession: {
                turns,
                clientId: typeof session.clientId === 'string'
                    ? session.clientId
                    : memory.value.promptSession.clientId,
                mode: session.mode === 'chat' ? 'chat' : (session.mode === 'suggestions' ? 'suggestions' : memory.value.promptSession.mode),
                savedAt: Date.now(),
            },
        };
        persistMemory();
    }

    function clearAssistantMemory() {
        if (import.meta.client && storagePrefix.value) {
            for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
                const key = window.localStorage.key(index);
                if (key?.startsWith(storagePrefix.value)) {
                    window.localStorage.removeItem(key);
                }
            }
        }
        memory.value = createEmptyState();
    }

    function isSuggestionDismissed(clientId: string, fingerprint: string, suggestion: AssistantSuggestion): boolean {
        const resolvedEntries = memory.value.resolved[clientId] || [];
        const signature = suggestionSignature(suggestion);
        const sourceMessageIds = (suggestion.sourceMessageIds || []).filter(Boolean);

        return resolvedEntries.some(entry => (
            assistantResolvedSuggestionMatches(entry, fingerprint, signature, sourceMessageIds)
        ));
    }

    function dismissedSuggestionCount(clientId: string, fingerprint: string): number {
        const cached = getCachedAnalysis(clientId, fingerprint);
        const suggestions = cached?.suggestions || [];
        return suggestions.filter((suggestion) => isSuggestionDismissed(clientId, fingerprint, suggestion)).length;
    }

    watch(storageKey, loadMemory, { immediate: true });

    return {
        getCachedAnalysis,
        getAvailableCachedAnalysis,
        setCachedAnalysis,
        dismissSuggestion,
        resolveSuggestions,
        clearDismissedSuggestions,
        getHandledMessageIds,
        getPromptSession,
        setPromptSession,
        clearAssistantMemory,
        isSuggestionDismissed,
        dismissedSuggestionCount,
    };
}
